import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environment.ts/environment';

export interface FileClientData {
  id_client: number;
  filename: string;
  filepath: string;
}

export interface FileClientResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    id_client: number;
    filename: string;
    filepath: string;
    created_at: string;
  };
  timestamp: string;
}

export interface ApiError {
  success: false;
  message: string;
  error?: string;
  details?: string;
  status: number;
  timestamp: string;
}

export interface FileClientItem {
  id: number;
  id_client: number;
  filename: string;
  filepath: string;
  created_at: string;
}

export interface GetFilesClientResponse {
  success: boolean;
  message: string;
  data: {
    id_client: number;
    files: FileClientItem[];
    count: number;
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileClientService {
  private readonly apiUrl = `${environment.fileConverterBaseUrl}/api/clients/file`;

  constructor(private http: HttpClient) {}

  /**
   * Crée un nouveau file_client en appelant l'API du backend
   * @param fileClientData - Données du file_client à insérer
   * @returns Observable<FileClientResponse> - Réponse de l'API
   */
  createFileClient(fileClientData: FileClientData): Observable<FileClientResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<FileClientResponse>(this.apiUrl, fileClientData, { headers })
      .pipe(
        map(response => {
          // Vérification que la réponse est bien formatée
          if (!response.success) {
            throw new Error(response.message || 'Erreur lors de la création du file_client');
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Gestion des erreurs HTTP
   * @param error - Erreur HTTP
   * @returns Observable<never>
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Une erreur inattendue s\'est produite';

    if (error.error instanceof ErrorEvent) {
      // Erreur côté client
      errorMessage = `Erreur: ${error.error.message}`;
    } else {
      // Erreur côté serveur
      if (error.error && typeof error.error === 'object') {
        const apiError = error.error as ApiError;
        errorMessage = apiError.message || apiError.error || `Erreur serveur: ${error.status}`;
      } else {
        errorMessage = `Erreur serveur: ${error.status} - ${error.statusText}`;
      }
    }

    console.error('Erreur dans FileClientService:', error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Valide les données avant envoi
   * @param fileClientData - Données à valider
   * @returns boolean - True si valide, false sinon
   */
  validateFileClientData(fileClientData: FileClientData): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validation de id_client
    if (!fileClientData.id_client || typeof fileClientData.id_client !== 'number' || fileClientData.id_client <= 0) {
      errors.push('id_client doit être un nombre entier positif');
    }

    // Validation de filename
    if (!fileClientData.filename || typeof fileClientData.filename !== 'string' || fileClientData.filename.trim().length === 0) {
      errors.push('filename est obligatoire et doit être une chaîne non vide');
    } else if (fileClientData.filename.length > 255) {
      errors.push('filename ne peut pas dépasser 255 caractères');
    }

    // Validation de filepath
    if (!fileClientData.filepath || typeof fileClientData.filepath !== 'string' || fileClientData.filepath.trim().length === 0) {
      errors.push('filepath est obligatoire et doit être une chaîne non vide');
    } else if (fileClientData.filepath.length > 255) {
      errors.push('filepath ne peut pas dépasser 255 caractères');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Crée un file_client avec validation préalable
   * @param fileClientData - Données du file_client
   * @returns Observable<FileClientResponse> - Réponse de l'API
   */
  createFileClientWithValidation(fileClientData: FileClientData): Observable<FileClientResponse> {
    // Validation des données
    const validation = this.validateFileClientData(fileClientData);
    
    if (!validation.valid) {
      return throwError(() => new Error(`Données invalides: ${validation.errors.join(', ')}`));
    }

    // Nettoyage des données
    const cleanedData: FileClientData = {
      id_client: fileClientData.id_client,
      filename: fileClientData.filename.trim(),
      filepath: fileClientData.filepath.trim()
    };

    return this.createFileClient(cleanedData);
  }

  /**
   * Récupère tous les files_client d'un client spécifique
   * @param id_client - ID du client
   * @returns Observable<GetFilesClientResponse> - Réponse de l'API
   */
  getFilesClientByClientId(id_client: number): Observable<GetFilesClientResponse> {
    // Validation de l'id_client
    if (!id_client || typeof id_client !== 'number' || id_client <= 0) {
      return throwError(() => new Error('id_client doit être un nombre entier positif'));
    }

    const url = `${this.apiUrl}/${id_client}`;
    
    return this.http.get<GetFilesClientResponse>(url)
      .pipe(
        map(response => {
          // Vérification que la réponse est bien formatée
          if (!response.success) {
            throw new Error(response.message || 'Erreur lors de la récupération des files_client');
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Récupère les files_client d'un client avec validation préalable
   * @param id_client - ID du client
   * @returns Observable<GetFilesClientResponse> - Réponse de l'API
   */
  getFilesClientByClientIdWithValidation(id_client: number): Observable<GetFilesClientResponse> {
    // Validation de l'id_client
    const validation = this.validateClientId(id_client);
    
    if (!validation.valid) {
      return throwError(() => new Error(`Données invalides: ${validation.errors.join(', ')}`));
    }

    return this.getFilesClientByClientId(id_client);
  }

  /**
   * Valide l'id_client
   * @param id_client - ID du client à valider
   * @returns { valid: boolean; errors: string[] } - Résultat de la validation
   */
  validateClientId(id_client: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!id_client || typeof id_client !== 'number' || id_client <= 0) {
      errors.push('id_client doit être un nombre entier positif');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

}
